import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { colors, fonts, spacing, borderRadius } from '../../../constants';

/**
 * DelegateDialog Component
 * Dialog for delegating/assigning queries to users
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Close handler
 * @param {Object} query - Query object
 * @param {Array} assignUsers - List of assignable users
 * @param {Array} assignAllUsers - All users (for region filtering)
 * @param {string} assignRegion - Selected region filter
 * @param {string} selectedAssigneeId - Selected assignee ID
 * @param {boolean} loadingAssignUsers - Whether users are loading
 * @param {string} assignReason - Assignment reason
 * @param {string} assignNote - Assignment note
 * @param {string} assigningId - ID of query being assigned
 * @param {function} onRegionChange - Region change handler
 * @param {function} onAssigneeChange - Assignee change handler
 * @param {function} onReasonChange - Reason change handler
 * @param {function} onNoteChange - Note change handler
 * @param {function} onConfirm - Confirm handler
 * @param {function} onCancel - Cancel handler
 */
const DelegateDialog = ({
  open,
  onClose,
  query,
  assignUsers,
  assignAllUsers,
  assignRegion,
  selectedAssigneeId,
  loadingAssignUsers,
  assignReason,
  assignNote,
  assigningId,
  onRegionChange,
  onAssigneeChange,
  onReasonChange,
  onNoteChange,
  onConfirm,
  onCancel,
}) => {
  // Get unique regions from all users
  const regions = React.useMemo(() => {
    const regionSet = new Set();
    if (Array.isArray(assignAllUsers)) {
      assignAllUsers.forEach((user) => {
        if (user.region) {
          regionSet.add(user.region);
        }
      });
    }
    return Array.from(regionSet).sort();
  }, [assignAllUsers]);

  return (
    <Dialog
      open={open}
      onClose={onCancel || onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: borderRadius.modal,
          boxShadow: `0 24px 48px ${colors.primaryShadow}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: colors.primaryGradient,
          color: colors.textWhite,
          fontWeight: fonts.weightBold,
          fontSize: fonts.fontSizeLg,
          padding: spacing.sm,
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.xs,
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: fonts.weightBold,
            fontSize: '1.5rem',
            letterSpacing: '-0.4px',
          }}
        >
          Delegate Query
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: spacing.md,
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.sm,
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 40%)',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: fonts.weightSemiBold,
            fontSize: fonts.fontSizeMd,
            color: colors.textPrimaryDark,
            mb: 0,
          }}
        >
          Select a delegate
        </Typography>
        {loadingAssignUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                color: colors.textPrimaryDark,
                fontSize: fonts.fontSizeSm,
                lineHeight: 1.6,
                mt: 0,
                mb: 0,
              }}
            >
              Choose the delegate who should receive the latest context and status updates for this query.
            </Typography>
            <FormControl 
              fullWidth 
              size="small"
              disabled={loadingAssignUsers || assignUsers.length === 0}
            >
              <Select
                value={selectedAssigneeId || ''}
                onChange={onAssigneeChange}
                displayEmpty
                disabled={assigningId === query?._id}
                renderValue={(selected) => {
                  if (!selected) {
                    return 'Assign To';
                  }
                  const user = assignUsers.find((option) => option._id === selected);
                  if (!user) return selected;
                  const userDisplayName = user.full_name || user.name || user.email || selected;
                  return `${userDisplayName} - ${(user.designation || user.role || '').toUpperCase()} (${user.region || 'NA'})`;
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '300px',
                      overflowY: 'auto',
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  fontSize: fonts.fontSizeSm,
                  backgroundColor: colors.backgroundWhite,
                  borderRadius: borderRadius.input,
                  border: `1px solid ${colors.borderDefault}`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: colors.borderHover,
                  },
                  '&.Mui-focused': {
                    borderColor: colors.primary,
                    borderWidth: '2px',
                    boxShadow: `0 0 0 3px ${colors.primaryTint}`,
                  },
                  '& .MuiSelect-select': {
                    padding: spacing.sm,
                    color: colors.textPrimaryDark,
                  },
                }}
              >
                {assignUsers.length === 0 ? (
                  <MenuItem disabled>No users available</MenuItem>
                ) : (
                  assignUsers.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.full_name  || 'Unknown User'}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: spacing.sm, borderTop: `1px solid ${colors.borderDefault}` }}>
        <Button
          onClick={onCancel || onClose}
          disabled={assigningId === query?._id}
          sx={{
            color: colors.textSecondary,
            textTransform: 'uppercase',
            fontWeight: fonts.weightSemiBold,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={!selectedAssigneeId || assigningId === query?._id}
          sx={{
            background: colors.primaryGradient,
            color: colors.primaryDark,
            textTransform: 'uppercase',
            fontWeight: fonts.weightBold,
            borderRadius: borderRadius.button,
            boxShadow: '0 4px 12px rgba(14, 23, 42, 0.2)',
            '&:hover': {
              background: colors.primaryGradient,
              boxShadow: '0 6px 16px rgba(14, 23, 42, 0.3)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              backgroundColor: colors.textDisabled,
              color: colors.textSecondary,
              boxShadow: 'none',
            },
          }}
        >
          {assigningId === query?._id ? 'Delegating...' : 'Delegate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelegateDialog;
